import React from "react";

import { Box, Typography } from "@material-ui/core";
import { UserSubscription } from "@remar/shared/dist/models";
import { IExtendedPaletteOptions } from "@remar/shared/dist/theme/default";
import { differenceInDays } from "date-fns";

import { SubscriptionStatusIcon, SubscriptionStatusWrapper } from "../../styles";

type subscriptionsTypes =
	| "No Subscription"
	| "RECURRING"
	| "CANCELLED INITIAL"
	| "CANCELLED RECURRING"
	| "TRAIL"
	| "INITIAL"
	| "PENDING CANCELLATION";

interface IStatusComponent {
	color: IExtendedPaletteOptions;
	text: subscriptionsTypes;
	subText?: string;
}

const StatusComponent = ({ color, text, subText }: IStatusComponent) => (
	<SubscriptionStatusWrapper>
		<Box display={"flex"} alignItems={"center"}>
			<SubscriptionStatusIcon color={color} /> <Typography className={"status"}>{text}</Typography>
		</Box>
		{subText && <Typography className={"subText"}>{subText}</Typography>}
	</SubscriptionStatusWrapper>
);

const SubscriptionStatus = ({ subscriptions }: { subscriptions: UserSubscription[] }) => {
	const currentSubs = (subscriptions: UserSubscription[]): UserSubscription => {
		const currentSubscription = (subscriptions || []).find(s => !s.hasExpired);
		return currentSubscription || ({} as UserSubscription);
	};
	const currentSubscription = currentSubs(subscriptions);
	const subscriptionType = currentSubscription.type;
	let daysLeft = -1;
	const expiration = currentSubscription.expiresOn;
	if (expiration) {
		daysLeft = differenceInDays(new Date(expiration), new Date());
	}
	if (currentSubscription.isCancelled) {
		return (
			<StatusComponent
				color={"warning"}
				text={"PENDING CANCELLATION"}
				subText={daysLeft > -1 ? `${daysLeft} active days left` : ""}
			/>
		);
	}

	if (!subscriptionType) {
		return <StatusComponent color={"info"} text={"No Subscription"} />;
	}

	if (subscriptionType.isRecurring) {
		return (
			<StatusComponent
				color={"success"}
				text={"RECURRING"}
				subText={daysLeft > -1 ? `${daysLeft} days till renewal` : ""}
			/>
		);
	}

	if (subscriptionType.isTrial) {
		const isCancelledRecurring = subscriptions.find(s => s.isCancelled && s.type!.isRecurring && s.hasExpired);
		const isCancelledInitial = subscriptions.find(
			s => s.isCancelled && !s.type?.isRecurring && !s.type?.isTrial && s.hasExpired
		);

		if (isCancelledRecurring) {
			return <StatusComponent color={"danger"} text={"CANCELLED RECURRING"} subText={"Trial"} />;
		}

		if (isCancelledInitial) {
			return <StatusComponent color={"danger"} text={"CANCELLED INITIAL"} subText={"Trial"} />;
		}
		return <StatusComponent color={"primary"} text={"TRAIL"} />;
	}

	return (
		<StatusComponent
			color={"success"}
			text={"INITIAL"}
			subText={daysLeft > -1 ? `${daysLeft} days till renewal` : ""}
		/>
	);
};

export default SubscriptionStatus;
